import { useQuery, useMutation, useQueryClient } from "react-query";
import { getEstates, getEstatesLookups, getEstateDetail, saveEstate, deleteEstate, getEstateOutData, postEstateOutAll } from "@/api/Maintainers/estates";
import { useApplyCreatedEstateAccess } from "@/hooks/general/useApplyCreatedEstateAccess";
import { mergeListFiltersWithNewIds } from "@/utils/estateFilterAccess";

export const key = "estates";

export function useEstatesLookups(options = {}) {
	return useQuery([key, "lookups"], () => getEstatesLookups(), options);
}

export function useEstatesList(filters, options = {}) {
	return useQuery([key, "list", filters], () => getEstates(filters), options);
}

export function useEstateDetail(slug, enabled) {
	return useQuery([key, "detail", slug], () => getEstateDetail(slug), {
		enabled: enabled && !!slug,
		keepPreviousData: true,
	});
}

export function useSaveEstate() {
	const queryClient = useQueryClient();
	const { applyCreatedEstateAccess } = useApplyCreatedEstateAccess();

	return useMutation(({ form, filters }) => saveEstate(form, filters), {
		onSuccess: (response, variables) => {
			const isCreate = !variables.form?.slug;
			let listFilters = variables.filters;

			if (isCreate && response?.detail?.id) {
				const estateParentId = response.detail.estate_parent_id ?? variables.form?.estateParentID ?? null;

				listFilters = mergeListFiltersWithNewIds(variables.filters, {
					estateParentId,
					estateIds: [response.detail.id],
				});

				const accessPayload = {
					estates: [
						{
							value: response.detail.id,
							label: response.detail.name,
							estate_parent_id: estateParentId ?? "",
						},
					],
				};

				if (estateParentId) {
					const lookups = queryClient.getQueryData([key, "lookups"]);
					const parentOptions = lookups?.data?.estates_parents ?? [];
					const foundParent = parentOptions.find((option) => Number(option.value) === Number(estateParentId));

					accessPayload.estateParent = {
						value: estateParentId,
						label: foundParent?.label ?? "",
					};
				}

				applyCreatedEstateAccess(accessPayload);
			}

			if (response?.data && listFilters) {
				queryClient.setQueryData([key, "list", listFilters], response);
			}

			if (response?.detail?.slug) {
				queryClient.setQueryData([key, "detail", response.detail.slug], { data: response.detail });
			}

			queryClient.invalidateQueries([key, "lookups"]);
		},
	});
}

export function useDeleteEstate() {
	const queryClient = useQueryClient();

	return useMutation(deleteEstate, {
		onSuccess: () => {
			queryClient.invalidateQueries([key, "list"]);
		},
	});
}

export function useEstateOutData(enabled) {
	return useQuery([key, "out-data"], () => getEstateOutData(), {
		enabled: enabled,
	});
}

export function useEstateOutAll() {
	return useMutation(postEstateOutAll);
}
